import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Check, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface Props {
	/** The minted one-time secret, or null when the dialog is closed. */
	token: string | null;
	keyName?: string;
	onClose: () => void;
}

/**
 * Shows a freshly-minted/regenerated API key secret exactly once. The plaintext token is never
 * retrievable again, so the dialog warns the user and offers a copy button before dismissal.
 */
export function MintedSecretDialog({ token, keyName, onClose }: Props) {
	const { t } = useTranslation();
	const [copied, setCopied] = useState(false);

	const copy = async () => {
		if (!token) return;
		try {
			await navigator.clipboard.writeText(token);
			setCopied(true);
			toast.success(t('system.apiKeys.copied'));
			setTimeout(() => setCopied(false), 2000);
		} catch {
			toast.error(t('system.apiKeys.copyFailed'));
		}
	};

	return (
		<Dialog open={Boolean(token)} onOpenChange={open => !open && onClose()}>
			<DialogContent showCloseButton={false}>
				<DialogHeader>
					<DialogTitle>{t('system.apiKeys.secretTitle', { name: keyName ?? '' })}</DialogTitle>
					<DialogDescription>{t('system.apiKeys.secretSubtitle')}</DialogDescription>
				</DialogHeader>

				<div className="flex items-start gap-2 rounded-md border border-warning bg-warning/10 px-3 py-2 text-xs text-warning-fg">
					<AlertTriangle size={15} className="mt-0.5 shrink-0" />
					<span>{t('system.apiKeys.secretWarning')}</span>
				</div>

				<div className="flex items-center gap-2 rounded-md border border-border bg-muted px-3 py-2">
					<code className="flex-1 break-all font-mono text-xs text-foreground">{token}</code>
					<Button type="button" variant="outline" size="icon" onClick={copy} aria-label={t('system.apiKeys.copy')}>
						{copied ? <Check size={15} className="text-success-fg" /> : <Copy size={15} />}
					</Button>
				</div>

				<DialogFooter>
					<Button type="button" onClick={onClose}>
						{t('system.apiKeys.secretDone')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
